import React, { useState } from 'react';
import styled from 'styled-components';
import { EngineOutput } from '../../model/model';
import FormBoxDropdown from './FormBoxDropdown';
import { netCostsInfo } from '../../helpers/tooltips';
import { PlayArrow } from '@mui/icons-material';

const PhaseContainer = styled.div`
    width: 100%;
    overflow: hidden;
`;

const DropdownContainer = styled.div`
    display: flex;
    flex: 1;
    position: relative;
`;

const Header = styled.div<{ $isExpanded: boolean, $isLastPhase: boolean }>`
    display: flex;
    border-bottom: ${({ $isExpanded, $isLastPhase }) => ($isExpanded ? '1px solid #58585A' : $isLastPhase ? 'none' : '1px solid #58585A')};
    cursor: pointer;
`;

const HeaderItem = styled.span`
    flex: 1;
    font-size: 0.9rem;
    padding: 1rem;
    text-align: center;
    &:not(:last-child) {
        border-right: 1px solid #58585A;
    }
`;
const MarginHeaderItem = styled(HeaderItem)`
    background-color: #E9ECF6;
    font-weight: 500;
    font-size: 0.9rem
`;

const PhaseNumberItem = styled.span`
    flex: 1;
    font-size: 0.9rem;
    text-align: center;
`;

const RotatingPlayArrow = styled(PlayArrow) <{ $isExpanded: boolean }>`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: ${({ $isExpanded }) => $isExpanded ? 'translateY(-50%) rotate(90deg)' : 'translateY(-50%) rotate(0deg)'};
    transition: transform 0.3s ease !important;
    cursor: pointer;
`;

const Content = styled.div<{ $isExpanded: boolean }>`
    max-height: ${({ $isExpanded }) => ($isExpanded ? '1500px' : '0')};
    opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
    transition: max-height 0.5s ease-in-out, opacity 0.3s ease-in-out;
    display: flex;
    align-items: center;
    justify-items: center;
    flex-direction: column;
`;

const OutputContainer = styled.div<{ $isLastPhase: boolean | undefined }>`
    display: flex;
    width: 100%;
    border-bottom: ${({ $isLastPhase }) => ($isLastPhase ? '' : '1px solid #58585A')};

`;

const OutputItemContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
`;

const OutputItem = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 0 25%;
    box-sizing: border-box;
    padding: 1rem;
    text-align: center;
    align-content: center;
    justify-content: center;
`;

const OutputItemHeader = styled.span`
    font-size: 1rem;
    padding: 1rem;
    text-align: center;
    border: none;
`;

const OutputItemContent = styled.span`
    font-size: 1rem;
    padding: 0.5rem;
    text-align: center;
    border: 1px solid #58585A;
    border-radius: 30px
`;

interface Props {
    phaseNumber: number;
    engineOutput: EngineOutput;
    isLastPhase: boolean;
}

const PhaseOutput: React.FC<Props> = ({ phaseNumber, engineOutput, isLastPhase }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const phaseProductNetSmartProfit = engineOutput.phaseProductNetSmartProfit.toFixed(2);
    const phaseProductNetNonSmartProfit = engineOutput.phaseProductNetNonSmartProfit.toFixed(2);
    const phaseHighlightCommissionNet = engineOutput.phaseHighlightCommissionNet.toFixed(2);
    const phaseHighlightExtraNet = engineOutput.phaseHighlightExtraNet.toFixed(2);
    const phasePromoteNet = engineOutput.phasePromoteNet.toFixed(2);
    const phaseSpecialZoneCommissionNet = engineOutput.phaseSpecialZoneCommissionNet.toFixed(2);
    const phaseSpecialZoneMarkNet = engineOutput.phaseSpecialZoneMarkNet.toFixed(2);
    const phaseCoinsNet = engineOutput.phaseCoinsNet.toFixed(2);
    const phaseAllegroPayNet = engineOutput.phaseAllegroPayNet.toFixed(2);
    const phaseAllegroAdsNet = engineOutput.phaseAllegroAdsNet.toFixed(2);
    const phasePublishingNet = engineOutput.phasePublishingNet.toFixed(2);
    const unitaryTransactionCostNet = engineOutput.unitaryTransactionCostNet.toFixed(2);
    const phaseCommissionNet = engineOutput.phaseCommissionNet.toFixed(2);
    const phaseDeliveryNet = engineOutput.phaseDeliveryNet.toFixed(2);
    const packagingCostNet = engineOutput.packagingCostNet.toFixed(2);
    const phaseIncomeTaxNet = engineOutput.phaseIncomeTaxNet.toFixed(2);
    const phaseCompanyZusNet = engineOutput.phaseCompanyZusNet.toFixed(2);
    const phaseMarginNetSmart = (engineOutput.phaseMarginNetSmart * 100).toFixed(0);
    const phaseMarginNetNonSmart = (engineOutput.phaseMarginNetNonSmart * 100).toFixed(0);
    const productCostNet = engineOutput.productCostNet.toFixed(2);

    return (
        <PhaseContainer>
            <Header $isExpanded={isExpanded} $isLastPhase={isLastPhase} onClick={() => setIsExpanded(!isExpanded)}>
                <HeaderItem>
                    <DropdownContainer>
                        <PhaseNumberItem>{phaseNumber}</PhaseNumberItem>
                        <RotatingPlayArrow fontSize='medium' $isExpanded={isExpanded} />
                    </DropdownContainer>
                </HeaderItem>
                <HeaderItem>{phaseProductNetSmartProfit} zł</HeaderItem>
                <HeaderItem>{phaseProductNetNonSmartProfit} zł</HeaderItem>
            </Header>
            <Content $isExpanded={isExpanded}>
                {isExpanded && <>
                    <OutputContainer $isLastPhase={false}>
                        <MarginHeaderItem>MARŻA</MarginHeaderItem>
                        <HeaderItem>{phaseMarginNetSmart} %</HeaderItem>
                        <HeaderItem>{phaseMarginNetNonSmart} %</HeaderItem>
                    </OutputContainer>
                    <OutputContainer $isLastPhase={false} >
                        <FormBoxDropdown variant="output" headerContent="OPŁATY ALLEGRO (NETTO)" tooltip={netCostsInfo}>
                            <OutputItemContainer>
                                <OutputItem>
                                    <OutputItemHeader> <br /> Prowizja</OutputItemHeader>
                                    <OutputItemContent>{phaseCommissionNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader>Wyróżnienie - dod. prowizja</OutputItemHeader>
                                    <OutputItemContent>{phaseHighlightCommissionNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader>Wyróżnienie - 19 dniowa opłata</OutputItemHeader>
                                    <OutputItemContent>{phaseHighlightExtraNet} zł</OutputItemContent>
                                </OutputItem>
                                {/* <OutputItem>
                                    <OutputItemHeader>Strefa okazji - dod. prowizja</OutputItemHeader>
                                    <OutputItemContent>{phaseSpecialZoneCommissionNet} zł</OutputItemContent>
                                </OutputItem> */}
                                <OutputItem>
                                    <OutputItemHeader>Promowanie na stronie działu</OutputItemHeader>
                                    <OutputItemContent>{phasePromoteNet} zł</OutputItemContent>
                                </OutputItem>
                                {/* <OutputItem>
                                    <OutputItemHeader>Oznaczenie Strefa okazji</OutputItemHeader>
                                    <OutputItemContent>{phaseSpecialZoneMarkNet} zł</OutputItemContent>
                                </OutputItem> */}
                                <OutputItem>
                                    <OutputItemHeader><br /> Koszt monet</OutputItemHeader>
                                    <OutputItemContent>{phaseCoinsNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader><br /> Allegro Pay</OutputItemHeader>
                                    <OutputItemContent>{phaseAllegroPayNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader><br /> Allegro Ads</OutputItemHeader>
                                    <OutputItemContent>{phaseAllegroAdsNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader>Wystawienie <br /> oferty</OutputItemHeader>
                                    <OutputItemContent>{phasePublishingNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader>Koszt <br /> transakcyjny</OutputItemHeader>
                                    <OutputItemContent>{unitaryTransactionCostNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem />
                                <OutputItem />
                                <OutputItem />
                            </OutputItemContainer>
                        </FormBoxDropdown>
                    </OutputContainer>
                    <OutputContainer $isLastPhase={false}>
                        <FormBoxDropdown variant="output" headerContent="OBSŁUGA KLIENTA (NETTO)" tooltip={netCostsInfo}>
                            <OutputItemContainer>
                                <OutputItem>
                                    <OutputItemHeader>Koszt dostawy</OutputItemHeader>
                                    <OutputItemContent>{phaseDeliveryNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem>
                                    <OutputItemHeader>Koszt pakowania</OutputItemHeader>
                                    <OutputItemContent>{packagingCostNet} zł</OutputItemContent>
                                </OutputItem>
                                <OutputItem />
                                <OutputItem />
                            </OutputItemContainer>
                        </FormBoxDropdown>
                    </OutputContainer>
                    <OutputContainer $isLastPhase={isLastPhase}>
                        {!isLastPhase &&
                            <FormBoxDropdown variant="output" headerContent="PODATEK DOCHODOWY I POZOSTAŁE OBCIĄZENIA (NETTO)" tooltip={netCostsInfo}>
                                <OutputItemContainer>
                                    <OutputItem>
                                        <OutputItemHeader>Podatek <br /> dochodowy</OutputItemHeader>
                                        <OutputItemContent>{phaseIncomeTaxNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem>
                                        <OutputItemHeader><br />ZUS</OutputItemHeader>
                                        <OutputItemContent>{phaseCompanyZusNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem>
                                        <OutputItemHeader>Wartość sprzed. towaru (netto)</OutputItemHeader>
                                        <OutputItemContent>{productCostNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem />
                                </OutputItemContainer>
                            </FormBoxDropdown>
                        }
                        {isLastPhase &&
                            <FormBoxDropdown variant="output" headerContent="PODATEK DOCHODOWY I POZOSTAŁE OBCIĄŻENIA (NETTO)" tooltip={netCostsInfo} isLastPhase={true}>
                                <OutputItemContainer>
                                    <OutputItem>
                                        <OutputItemHeader>Podatek <br /> dochodowy</OutputItemHeader>
                                        <OutputItemContent>{phaseIncomeTaxNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem>
                                        <OutputItemHeader><br />ZUS</OutputItemHeader>
                                        <OutputItemContent>{phaseCompanyZusNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem>
                                        <OutputItemHeader>Wartość sprzedanego towaru (netto)</OutputItemHeader>
                                        <OutputItemContent>{productCostNet} zł</OutputItemContent>
                                    </OutputItem>
                                    <OutputItem />
                                </OutputItemContainer>
                            </FormBoxDropdown>
                        }
                    </OutputContainer>
                </>}
            </Content>
        </PhaseContainer >
    );
}

export default PhaseOutput;
